import { SparkleIcon, CheckIcon } from "./Icons";

/**
 * Dark-themed hero illustration: a stylized but readable mockup of the
 * AI-drafted-reply moment — the product's core value in one glance.
 *
 * Central card: real ticket header, real customer message, real AI draft
 * with grounded-in chip, real Send / Edit buttons.
 *
 * Floating overlay chips (kargo-style): "New · Gmail" (incoming), customer
 * context (Priya · LTV), "Reply sent" (outcome). Together they tell the loop
 * without a paragraph of explainer copy.
 */
export default function HeroIllustration() {
  return (
    <div
      role="img"
      aria-label="ZopDesk composing an AI-drafted reply to an inbound customer ticket"
      className="relative mx-auto w-full max-w-[540px]"
    >
      {/* Backdrop glow */}
      <div className="pointer-events-none absolute inset-x-10 top-4 -z-10 h-4/5 rounded-[50%] bg-gradient-to-br from-brand-500/30 via-violet-500/20 to-transparent blur-3xl" />

      {/* ── Main card: AI draft moment ───────────────────────── */}
      <div className="relative mx-auto w-[88%] max-w-[420px] overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-ink-800 to-ink-900 shadow-2xl shadow-black/50">
        {/* Window bar */}
        <div className="flex items-center gap-1.5 border-b border-white/5 bg-white/[0.02] px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
          <span className="ml-3 font-mono text-[10px] font-medium text-ink-400">
            ticket · #2048
          </span>
        </div>

        {/* Ticket header: avatar + name + priority */}
        <div className="flex items-start justify-between px-5 pt-5">
          <div className="flex items-center gap-3">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-violet-500 text-sm font-semibold text-white">
              PS
            </div>
            <div>
              <p className="text-sm font-semibold text-white leading-tight">
                Priya Sharma
              </p>
              <p className="text-[11px] text-ink-400">
                priya@acmeretail.com · 2m ago
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-2.5 py-1 text-[10px] font-semibold text-rose-300 ring-1 ring-rose-500/30">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400" />
            High priority
          </span>
        </div>

        {/* Customer message bubble */}
        <div className="mx-5 mt-4 rounded-xl border border-white/5 bg-white/[0.03] p-3.5">
          <p className="text-[12.5px] leading-relaxed text-ink-200">
            Hi — I ordered the linen set on Tuesday but received the wrong size.
            Can I get a replacement or a refund?
          </p>
        </div>

        {/* AI Draft card */}
        <div className="relative mx-5 mt-3 rounded-xl border border-dashed border-brand-400/50 bg-gradient-to-br from-brand-500/10 via-transparent to-violet-500/10 p-3.5">
          <div className="mb-2.5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-brand-500 to-violet-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm shadow-brand-500/40">
              <SparkleIcon width={10} height={10} className="animate-spin-slow" />
              AI Draft
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300 ring-1 ring-amber-400/30">
              From the thread · edit before sending
            </span>
          </div>
          <p className="text-[12.5px] leading-relaxed text-ink-100">
            Hi Priya — so sorry the linen set arrived in the wrong size. We can
            send a replacement in the right size, or refund the order. Which
            would you prefer?
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 px-5 pt-4 pb-5">
          <button
            type="button"
            className="rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-md shadow-brand-500/40 pointer-events-none"
          >
            Send reply
          </button>
          <button
            type="button"
            className="rounded-md border border-white/10 bg-white/5 px-3.5 py-1.5 text-[12px] font-semibold text-ink-200 pointer-events-none"
          >
            Edit
          </button>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-ink-400">
            <kbd className="rounded border border-white/10 bg-white/5 px-1.5 py-0.5 font-mono text-[10px]">
              ⌘
            </kbd>
            <kbd className="rounded border border-white/10 bg-white/5 px-1.5 py-0.5 font-mono text-[10px]">
              ⏎
            </kbd>
          </span>
        </div>
      </div>

      {/* ── Floating chip 1: New ticket (top-left) ───────────── */}
      <div className="animate-float-slow absolute -left-2 top-10 hidden w-[180px] rounded-xl border border-white/10 bg-ink-900/90 p-3 shadow-2xl shadow-black/50 backdrop-blur sm:block">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          <p className="text-[11px] font-semibold text-white">New · Gmail</p>
          <span className="ml-auto text-[10px] text-ink-500">0s</span>
        </div>
        <p className="mt-1 truncate text-[10.5px] text-ink-400">
          priya@acmeretail.com
        </p>
        <p className="mt-0.5 truncate text-[10.5px] text-ink-500">
          Re: Linen set — wrong size
        </p>
      </div>

      {/* ── Floating chip 2: Customer context (top-right) ──── */}
      <div
        className="animate-float absolute -right-2 -top-2 hidden w-[200px] rounded-xl border border-white/10 bg-ink-900/90 p-3 shadow-2xl shadow-black/50 backdrop-blur sm:block"
        style={{ animationDelay: "1.5s" }}
      >
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-brand-300">
          Shopify context
        </p>
        <div className="flex items-center gap-2">
          <div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-violet-500 text-[10px] font-semibold text-white">
            PS
          </div>
          <div className="min-w-0">
            <p className="truncate text-[11px] font-semibold text-white">
              Priya Sharma
            </p>
            <p className="truncate text-[10px] text-ink-400">Shopify customer</p>
          </div>
        </div>
        <div className="mt-2.5 grid grid-cols-3 gap-1.5 text-center">
          <div className="rounded bg-white/5 px-1 py-1">
            <p className="text-[8px] text-ink-500">Orders</p>
            <p className="text-[11px] font-semibold text-white">3</p>
          </div>
          <div className="rounded bg-white/5 px-1 py-1">
            <p className="text-[8px] text-ink-500">LTV</p>
            <p className="text-[11px] font-semibold text-white">$412</p>
          </div>
          <div className="rounded bg-white/5 px-1 py-1">
            <p className="text-[8px] text-ink-500">Last</p>
            <p className="text-[11px] font-semibold text-white">5d</p>
          </div>
        </div>
      </div>

      {/* ── Floating chip 3: Reply sent (bottom-right) ───────── */}
      <div
        className="animate-float-slow absolute -right-4 bottom-6 hidden w-[180px] rounded-xl border border-emerald-500/40 bg-emerald-500/15 p-3 shadow-2xl shadow-emerald-500/10 backdrop-blur sm:block"
        style={{ animationDelay: "3s" }}
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white shadow-md shadow-emerald-500/40">
            <CheckIcon width={14} height={14} strokeWidth={2.6} />
          </span>
          <div>
            <p className="text-[11px] font-semibold text-emerald-200">
              Reply sent
            </p>
            <p className="text-[10px] text-emerald-400/70">
              In the same email thread
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
